import React, {useCallback, useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {Redirect} from 'react-router-dom';
import {Badge, Button, Col, Container, Form, Modal, Row} from 'react-bootstrap';
import {FaPlus, FaSave, FaSyncAlt, FaTrophy, FaUsers} from 'react-icons/fa';
import {useTranslation} from 'react-i18next';
import CommonMetaTag from './common-meta-tag';
import {getOpenMatches, getPlayerSportRankings, joinOpenMatch, leaveOpenMatch, savePlayerSportRankingQuestionnaire} from '../api';
import Configuration from '../config';
import {mergeRankingQuestionnaires, rankingOptionLabel, rankingQuestionLabel} from '../constants/player-ranking-questionnaires';
import {hasInvitedLevelOverride} from '../util/open-match-invitations';

const sportOptions = [
  {value: '', label: 'Tutti gli sport'},
  {value: 'tennis', label: 'Tennis'},
  {value: 'padel', label: 'Padel'},
  {value: 'pickleball', label: 'Pickleball'}
];

const matchTypeLabel = {
  friendly: 'OPEN_MATCH_TYPE_FRIENDLY',
  competitive: 'OPEN_MATCH_TYPE_COMPETITIVE'
};

const sportLabel = {
  tennis: 'Tennis',
  padel: 'Padel',
  pickleball: 'Pickleball'
};

const normalizeSport = value => String(value || '')
  .toLowerCase()
  .trim()
  .replace(/^sport[-_\s]*/, '')
  .replace(/^replayer[-_\s]*/, '');

const sportDisplayName = (sport, t) => {
  const normalized = normalizeSport(sport);
  return sportLabel[normalized] || normalized;
};

const formatMatchDate = (value, locale) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(locale, {weekday: 'long', day: '2-digit', month: 'long'});
};

const blockedLabel = (reason, t) => {
  if (reason === 'ranking_required') return t('OPEN_MATCH_CONFIGURE_RANKING');
  if (reason === 'level_too_low') return t('OPEN_MATCH_LEVEL_TOO_LOW');
  if (reason === 'level_too_high') return t('OPEN_MATCH_LEVEL_TOO_HIGH');
  if (reason === 'not_enough_available_credit') return t('INSUFFICIENT_CREDIT');
  if (reason === 'open_match_full') return t('OPEN_MATCH_FULL_SHORT');
  return t('OPEN_MATCH_NOT_COMPATIBLE');
};

const creditErrorMessage = (credit, t) => {
  if (!credit) return t('OPEN_MATCH_CREDIT_INSUFFICIENT_JOIN');
  return t('OPEN_MATCH_CREDIT_INSUFFICIENT_DETAIL', {
    wallet: credit.walletEur.toFixed(2),
    held: credit.heldEur.toFixed(2),
    required: credit.requiredEur.toFixed(2)
  });
};

const formatMatchTime = value => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'});
};

const partnerInitials = name => String(name || 'R')
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map(part => part.charAt(0).toUpperCase())
  .join('');

const partnerLogoUrl = match => {
  const logo = match && (match.partnerLogoUrl || match.logoUrl || match.partnerLogo);
  if (!logo) return null;
  if (/^https?:\/\//.test(logo) || logo.startsWith('/')) return logo;
  const partnerId = match.partnerId || match.partnerUid;
  return partnerId ? `${Configuration.apiEndPoint}/images/${partnerId}?load=${encodeURIComponent(logo)}` : null;
};

const PlayerOpenMatches = ({profile, token}) => {
  const {t, i18n} = useTranslation();
  const [sport, setSport] = useState('');
  const [matches, setMatches] = useState([]);
  const [questionnaires, setQuestionnaires] = useState({});
  const [rankingModalMatch, setRankingModalMatch] = useState(null);
  const [rankingAnswers, setRankingAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [savingRanking, setSavingRanking] = useState(false);
  const [message, setMessage] = useState(null);

  const loadMatches = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    const res = await getOpenMatches({sport}, token);
    if (res.status === 200) {
      setMatches(res.json.openMatches || []);
    } else {
      setMessage(t('OPEN_MATCHES_LOAD_FAILED', {error: res.json && res.json.error ? res.json.error : res.status}));
    }
    setLoading(false);
  }, [sport, t, token]);

  useEffect(() => {
    loadMatches();
  }, [loadMatches]);

  useEffect(() => {
    const loadRankingSetup = async () => {
      const res = await getPlayerSportRankings(token);
      if (res.status === 200) {
        setQuestionnaires(mergeRankingQuestionnaires(res.json.questionnaires));
      }
    };
    if (token) {
      loadRankingSetup();
    }
  }, [token]);

  const availableMatches = useMemo(() => {
    const now = Date.now();
    return matches
      .filter(match => {
        const startTime = match.startDatetime ? new Date(match.startDatetime).getTime() : 0;
        return match.status === 'open'
          && startTime > now
          && Number(match.playersJoined || 0) < Number(match.playersRequired || 0);
      })
      .sort((a, b) => new Date(a.startDatetime) - new Date(b.startDatetime));
  }, [matches]);

  if (!profile || !token) {
    return <Redirect to="/login"/>;
  }

  const handleJoin = async openMatchId => {
    const res = await joinOpenMatch(openMatchId, token);
    if (res.status === 200) {
      await loadMatches();
    } else {
      const reason = res.json && (res.json.reason || res.json.error);
      if (reason === 'not_enough_available_credit') {
        setMessage(creditErrorMessage(res.json.credit, t));
      } else if (reason === 'slot_no_longer_available') {
        setMessage(t('OPEN_MATCH_SLOT_CANCELLED_RELEASED'));
        await loadMatches();
      } else {
        setMessage(t('OPEN_MATCH_JOIN_FAILED', {error: res.json && res.json.error ? res.json.error : res.status}));
      }
    }
  };

  const handleLeave = async openMatchId => {
    const res = await leaveOpenMatch(openMatchId, token);
    if (res.status === 200) {
      await loadMatches();
    } else {
      setMessage(t('OPEN_MATCH_LEAVE_FAILED', {error: res.json && res.json.error ? res.json.error : res.status}));
    }
  };

  const openRankingModal = match => {
    setRankingModalMatch(match);
    setRankingAnswers({});
    setMessage(null);
  };

  const saveRankingFromModal = async () => {
    if (!rankingModalMatch) return;

    const questionnaire = questionnaires[rankingModalMatch.sport] || {questions: []};
    const allAnswered = questionnaire.questions.every(question => rankingAnswers[question.key] !== undefined);
    if (!allAnswered) {
      setMessage(t('RANKING_COMPLETE_ALL_QUESTIONS'));
      return;
    }

    setSavingRanking(true);
    const answers = questionnaire.questions.map(question => ({
      questionKey: question.key,
      value: rankingAnswers[question.key]
    }));
    const res = await savePlayerSportRankingQuestionnaire(rankingModalMatch.sport, answers, token);
    if (res.status === 200) {
      setRankingModalMatch(null);
      setRankingAnswers({});
      await loadMatches();
      setMessage(t('OPEN_MATCH_RANKING_CONFIGURED'));
    } else {
      setMessage(t('RANKING_SAVE_FAILED', {error: res.json && res.json.error ? res.json.error : res.status}));
    }
    setSavingRanking(false);
  };

  const rankingQuestionnaire = rankingModalMatch ? questionnaires[rankingModalMatch.sport] || {questions: [], options: []} : {questions: [], options: []};

  return (
    <Container className="open-matches-page">
      <CommonMetaTag/>
      <div className="open-matches-hero">
        <span><FaUsers aria-hidden="true"/> {t('OPEN_MATCHES_PAGE_COMMUNITY')}</span>
        <h1>{t('OPEN_MATCHES')}</h1>
        <p>{t('OPEN_MATCHES_PAGE_INTRO')}</p>
      </div>
      <div className="open-matches-toolbar">
        <Form.Group>
          <Form.Label>{t('SPORT')}</Form.Label>
          <Form.Control as="select" value={sport} onChange={event => setSport(event.target.value)}>
            {sportOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
          </Form.Control>
        </Form.Group>
        <div className="open-matches-toolbar-actions">
          <Button type="button" className="open-matches-create-button" href="/open-matches/create">
            <FaPlus aria-hidden="true"/>{t('CREATE_MATCH')}
          </Button>
          <Button type="button" className="open-matches-refresh-button" onClick={loadMatches} disabled={loading}>
            <FaSyncAlt aria-hidden="true"/>{loading ? t('LOADING') : t('REFRESH')}
          </Button>
        </div>
      </div>
      {message ? <div className="open-matches-message">{message}</div> : null}
      <Row>
        {availableMatches.map(match => {
          const logoUrl = partnerLogoUrl(match);
          const invitedLevelOverride = hasInvitedLevelOverride(match);
          return (
          <Col key={match.id} xs={12} lg={6} className="mb-3">
            <div className="open-match-card">
              <div className="open-match-card-head">
                <span className="open-match-card-club-logo">
                  {logoUrl ? <img src={logoUrl} alt="" aria-hidden="true"/> : partnerInitials(match.partnerName)}
                </span>
                <div>
                  <span>{match.partnerName}</span>
                  <h3>{match.title || `${match.fieldName} - ${sportDisplayName(match.sport, t)}`}</h3>
                </div>
                <Badge className="open-match-card-type" variant={match.matchType === 'competitive' ? 'danger' : 'secondary'}>{t(matchTypeLabel[match.matchType] || match.matchType)}</Badge>
              </div>
              <div className="open-match-meta">
                <span className="open-match-meta-sport"><strong>{sportDisplayName(match.sport, t)}</strong></span>
                <span>{formatMatchDate(match.startDatetime, i18n.language || 'it-IT')} · {formatMatchTime(match.startDatetime)}-{formatMatchTime(match.endDatetime)}</span>
                <span>{match.fieldName}</span>
                <span>{t('OPEN_MATCH_PLAYERS_COUNT', {joined: match.playersJoined, required: match.playersRequired})}</span>
                <span>{t('OPEN_MATCH_LEVEL_RANGE', {min: match.minLevel || '-', max: match.maxLevel || '-'})}</span>
              </div>
              {match.notes ? <p>{match.notes}</p> : null}
              {match.joinBlockReason === 'not_enough_available_credit' ? (
                <div className="open-match-credit-warning">{creditErrorMessage(match.credit, t)}</div>
              ) : null}
              {invitedLevelOverride ? (
                <div className="open-match-level-override-warning">{t('OPEN_MATCH_INVITED_LEVEL_OVERRIDE_WARNING')}</div>
              ) : null}
              <div className="open-match-participants">
                {match.participants.map(participant => (
                  <span key={participant.uid}><FaTrophy aria-hidden="true"/>{participant.fullName}</span>
                ))}
              </div>
              <div className="open-match-actions">
                <Button type="button" className="open-match-view-button" href={`/matches/${match.publicId}`}>{t('VIEW_MATCH')}</Button>
                {match.currentUserJoined && match.creatorUid !== profile.uid ?
                  <Button type="button" variant="outline-secondary" onClick={() => handleLeave(match.id)}>{t('EXIT')}</Button> :
                  match.currentUserJoined ?
                    <Button type="button" variant="outline-secondary" disabled>{t('OPEN_MATCH_YOU_ARE_ORGANIZER')}</Button> :
                    <Button
                      type="button"
                      className="profile-auth-action profile-auth-action--save"
                      disabled={!match.canJoin && match.joinBlockReason !== 'ranking_required'}
                      onClick={() => match.canJoin ? handleJoin(match.id) : openRankingModal(match)}
                    >
                      {match.canJoin ? t(invitedLevelOverride ? 'OPEN_MATCH_JOIN_ANYWAY' : 'JOIN') : blockedLabel(match.joinBlockReason, t)}
                    </Button>
                }
              </div>
            </div>
          </Col>
          );
        })}
      </Row>
      {!loading && availableMatches.length === 0 ? <div className="open-matches-empty">{t('OPEN_MATCHES_EMPTY_AVAILABLE')}</div> : null}
      <Modal show={!!rankingModalMatch} onHide={() => setRankingModalMatch(null)} size="lg" centered dialogClassName="open-match-ranking-modal">
        <Modal.Header closeButton className="open-match-ranking-modal-header">
          <div>
            <span>{t('OPEN_MATCH_RANKING_REQUIRED')}</span>
          <Modal.Title>{t('CONFIGURE')} {rankingModalMatch ? t(sportLabel[rankingModalMatch.sport] || rankingModalMatch.sport) : ''}</Modal.Title>
          </div>
        </Modal.Header>
        <Modal.Body className="open-match-ranking-modal-body">
          <p className="open-match-ranking-intro">
            {t('OPEN_MATCH_RANKING_REQUIRED_BODY')}
          </p>
          {rankingQuestionnaire.questions.map(question => (
            <div key={question.key} className="player-ranking-question">
              <strong>{rankingQuestionLabel(question, t)}</strong>
              <div className="player-ranking-options">
                {(question.options || rankingQuestionnaire.options || []).map(option => (
                  <button
                    key={`${question.key}-${option.value}`}
                    type="button"
                    className={rankingAnswers[question.key] === option.value ? 'active' : ''}
                    onClick={() => setRankingAnswers({...rankingAnswers, [question.key]: option.value})}
                  >
                    {rankingOptionLabel(option, t)}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </Modal.Body>
        <Modal.Footer className="open-match-ranking-modal-footer">
          <Button variant="outline-secondary" className="open-match-ranking-cancel" onClick={() => setRankingModalMatch(null)}>{t('CANCEL')}</Button>
          <Button type="button" className="profile-auth-action profile-auth-action--save" disabled={savingRanking} onClick={saveRankingFromModal}>
            <FaSave aria-hidden="true"/>{savingRanking ? t('SAVING') : t('SAVE_RANKING')}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

PlayerOpenMatches.propTypes = {
  profile: PropTypes.object,
  token: PropTypes.string,
};

PlayerOpenMatches.defaultProps = {
  profile: null,
  token: null,
};

const mapStateToProps = ({login: {profile, token}}) => ({profile, token});

export default connect(mapStateToProps)(PlayerOpenMatches);
